import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from "./TimePickerInput.module.css";
import { useAppStore } from "../../store/appStore";

// Custom TimePickerInput component
// Formats inputs to HH:mm format, with validation and formatting
// If input is not in a convertable format, it will be cleared, and an alert will be shown
// If the input is empty, it will be cleared, and the dropdown will be closed
export const TimePickerInput = ({
  value,
  onChange,
  disabled,
  className,
  interval = 15, // Controls both dropdown increments and rounding behavior
}) => {
  // Validate that interval divides evenly into 1440 (total minutes in a day)
  if (1440 % interval !== 0) {
    console.error(
      `TimePickerInput: interval (${interval}) must divide evenly into 1440 minutes. Defaulting to 15 minutes.`
    );
    interval = 15;
  }
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);

  // Sync internal state when external value prop changes
  // Skip sync while user is actively typing to prevent cursor jumping
  useEffect(() => {
    if (userHasTyped) return;
    setInputValue(value || "");
  }, [value, userHasTyped]);

  // Generate time options based on the interval for users to select from in the dropdown
  const timeOptions = Array.from({ length: 1440 / interval }, (_, index) => {
    const totalMinutes = index * interval;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  });

  // Function to update dropdown position based on input element
  const updateDropdownPosition = () => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportHeight =
        viewport && typeof viewport.height === "number"
          ? viewport.height
          : window.innerHeight;
      const offsetTop =
        viewport && typeof viewport.offsetTop === "number"
          ? viewport.offsetTop
          : 0;
      const offsetLeft =
        viewport && typeof viewport.offsetLeft === "number"
          ? viewport.offsetLeft
          : 0;

      // Determine if we should show dropdown above or below the input
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownHeight = Math.min(200, timeOptions.length * 36); // approximate height

      const position = {
        left: rect.left + (isTouch ? offsetLeft : 0),
        width: rect.width,
      };
      if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
        position.top = rect.top - dropdownHeight + (isTouch ? offsetTop : 0);
      } else {
        position.top = rect.bottom + (isTouch ? offsetTop : 0);
      }
      setDropdownPosition(position);
    }
  };

  // Find the index of a time value in the timeOptions array
  const findTimeIndex = (timeValue) => {
    if (!timeValue) return 0;
    const index = timeOptions.findIndex((option) => option === timeValue);
    return index >= 0 ? index : 0;
  };

  // Update position when dropdown is shown
  useEffect(() => {
    if (showDropdown) {
      updateDropdownPosition();
      // Initialize highlighted index when opening - highlight current value if it exists
      setHighlightedIndex((prev) => {
        if (prev !== null) return prev;
        // If there's a current value, try to find and highlight it
        if (inputValue) {
          const currentIndex = findTimeIndex(inputValue);
          return currentIndex;
        }
        return 0;
      });
    }
  }, [showDropdown, inputValue]);

  // Add scroll/resize listeners to update position during scrolling and viewport changes
  useEffect(() => {
    if (showDropdown) {
      // Use both window scroll and capturing phase for all scrollable elements
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      if (window.visualViewport) {
        window.visualViewport.addEventListener(
          "resize",
          updateDropdownPosition
        );
      }

      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener(
            "resize",
            updateDropdownPosition
          );
        }
      };
    }
  }, [showDropdown]);

  // Ensure highlighted item stays in view within the dropdown
  useEffect(() => {
    if (!showDropdown || highlightedIndex == null || !dropdownRef.current) {
      return;
    }
    const items = dropdownRef.current.querySelectorAll("li");
    const item = items[highlightedIndex];
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex, showDropdown]);

  // Handle clicks outside of dropdown so the dropdown closes and the input is cleared
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if refs are still valid before proceeding
      if (!dropdownRef.current || !inputRef.current) {
        setShowDropdown(false);
        return;
      }

      if (
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    // Add event listeners for both mouse and touch events, for mobile and desktop
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Everything to deal with formatting the input
  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = (timeStr) => {
    const cleaned = timeStr.toLowerCase().trim();

    // First, try to match formats with AM/PM indicators, including compact formats like "331p", "1245am"
    // This regex handles: "3p", "3pm", "3:30pm", "330p", "330pm", "1245a", "1245am", etc.
    const matchWithPeriod = cleaned.match(/^(\d{1,4})\s*(a|am|p|pm)$/);

    if (matchWithPeriod) {
      let [_, timeDigits, period] = matchWithPeriod;
      let hours, minutes;

      // Parse the time digits based on length
      if (timeDigits.length === 1 || timeDigits.length === 2) {
        // "3p" or "12pm" → hour only
        hours = parseInt(timeDigits);
        minutes = "00";
      } else if (timeDigits.length === 3) {
        // "331p" → 3:31pm
        hours = parseInt(timeDigits.slice(0, 1));
        minutes = timeDigits.slice(1);
      } else if (timeDigits.length === 4) {
        // "1245a" → 12:45am
        hours = parseInt(timeDigits.slice(0, 2));
        minutes = timeDigits.slice(2);
      }

      // Validate hours and minutes
      if (hours > 12 || parseInt(minutes) > 59) {
        return null;
      }

      // Convert to 24-hour format
      if ((period === "p" || period === "pm") && hours < 12) hours += 12;
      if ((period === "a" || period === "am") && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, "0")}:${minutes.padStart(2, "0")}`;
    }

    // Then try standard format with optional colon: "3", "3:30", "15:30"
    const match = cleaned.match(/^(\d{1,2})(?::(\d{2}))?$/);

    if (!match) return null;

    let [_, hours, minutes = "00"] = match;
    hours = parseInt(hours);

    // Dealing with hour input greater than 24
    if (hours > 24) {
      return null;
    }

    // Return the time in 24-hour format, padded to 2 digits if necessary.
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Round time to nearest interval based on the interval parameter
  const roundToNearestInterval = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    let totalMinutes = hours * 60 + minutes;

    // Round to nearest interval increment
    const remainder = totalMinutes % interval;
    const halfInterval = interval / 2;

    if (remainder >= halfInterval) {
      // Round up if at or past half of the interval
      totalMinutes = totalMinutes + (interval - remainder);
    } else {
      // Round down if less than half of the interval
      totalMinutes = totalMinutes - remainder;
    }

    // Handle case where rounding pushes us into next day
    if (totalMinutes >= 24 * 60) {
      return "00:00";
    }

    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;

    return `${newHours.toString().padStart(2, "0")}:${newMinutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Validate and format time input
  const validateAndFormatTime = (input) => {
    // First, try parsing as 12-hour format (handles "3p", "3 pm", "3:30 pm", etc.)
    const converted = convertTo24Hour(input);
    if (converted) {
      return roundToNearestInterval(converted);
    }

    // If that fails, try handling pure numeric formats
    // Remove all non-digits and colons
    const cleaned = input.replace(/[^\d:]/g, "");

    // Handle HHMM format (like "2359" or "425")
    if (!cleaned.includes(":")) {
      if (cleaned.length === 4) {
        const hours = cleaned.slice(0, 2);
        const minutes = cleaned.slice(2);
        return validateAndFormatTime(`${hours}:${minutes}`);
      } else if (cleaned.length === 3) {
        const hours = cleaned.slice(0, 1);
        const minutes = cleaned.slice(1);
        return validateAndFormatTime(`${hours}:${minutes}`);
      } else if (cleaned.length === 1 || cleaned.length === 2) {
        // Handle single digit or double digit hour input (like "3" -> "03:00")
        const hours = parseInt(cleaned);
        if (hours >= 0 && hours <= 23) {
          return roundToNearestInterval(
            `${hours.toString().padStart(2, "0")}:00`
          );
        }
      }
    }

    // Handle HH:MM format that's already been cleaned
    if (cleaned.includes(":")) {
      const [hourStr, minuteStr] = cleaned.split(":");
      const hours = parseInt(hourStr);
      const minutes = parseInt(minuteStr);

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
        return roundToNearestInterval(timeStr);
      }
    }

    return null;
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setUserHasTyped(true); // Mark that user has actively typed

    // Don't validate empty input
    if (!newValue) {
      try {
        onChange("");
      } catch (error) {
        console.warn(
          "TimePickerInput: onChange callback failed during input change:",
          error
        );
      }
      setUserHasTyped(false);
      return;
    }

    const formattedTime = validateAndFormatTime(newValue);
    if (formattedTime) {
      try {
        onChange(formattedTime);
      } catch (error) {
        console.warn(
          "TimePickerInput: onChange callback failed during validation:",
          error
        );
      }
    }
  };

  const handleInputBlur = () => {
    // Skipping validation if a dropdown value is clicked. This was causing the dropdown values to be overridden.
    if (dropdownRef.current?.contains(document.activeElement)) {
      return;
    }
    setTimeout(() => {
      // Check if component is still mounted and onChange is still available
      if (!inputRef.current || typeof onChange !== "function") {
        // Component has been unmounted or parent context is gone, just cleanup
        setShowDropdown(false);
        setUserHasTyped(false);
        return;
      }

      if (!inputValue) {
        // Handle empty input
        try {
          onChange("");
        } catch (error) {
          console.warn(
            "TimePickerInput: onChange callback failed during blur:",
            error
          );
        }
        setShowDropdown(false);
        setUserHasTyped(false);
        return;
      }
      const formattedTime = validateAndFormatTime(inputValue);
      if (!formattedTime) {
        // Invalid time format - clear both input and parent data
        setInputValue("");
        try {
          onChange("");
        } catch (error) {
          console.warn(
            "TimePickerInput: onChange callback failed during validation:",
            error
          );
        }
        setShowDropdown(false);
        setUserHasTyped(false);
        alert(
          "Please enter time in HH:mm format (24-hour) or use the dropdown"
        );
        return;
      }

      // Update both the input display and the parent component
      setInputValue(formattedTime);
      try {
        onChange(formattedTime);
      } catch (error) {
        console.warn(
          "TimePickerInput: onChange callback failed during update:",
          error
        );
      }
      setShowDropdown(false);
      setUserHasTyped(false); // Reset flag after successful validation
    }, 100);
  };

  const handleContainerMouseDown = (e) => {
    // If clicking a time option, prevent the blur event
    if (e.target.classList.contains(styles.timeOption)) {
      e.preventDefault();
      const time = e.target.textContent;
      setInputValue(time);
      try {
        onChange(time);
      } catch (error) {
        console.warn(
          "TimePickerInput: onChange callback failed during dropdown selection:",
          error
        );
      }
      setShowDropdown(false);
      setUserHasTyped(false); // Reset flag since user selected from dropdown
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    const maxIndex = timeOptions.length - 1;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setShowDropdown(true);
      setHighlightedIndex((prev) => {
        if (prev == null) return 0;
        return prev >= maxIndex ? maxIndex : prev + 1;
      });
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setShowDropdown(true);
      setHighlightedIndex((prev) => {
        if (prev == null) return maxIndex;
        return prev <= 0 ? 0 : prev - 1;
      });
      return;
    }

    if (e.key === "Enter") {
      if (showDropdown && highlightedIndex != null) {
        e.preventDefault();
        const time = timeOptions[highlightedIndex];
        setInputValue(time);
        try {
          onChange(time);
        } catch (error) {
          console.warn(
            "TimePickerInput: onChange callback failed during Enter key selection:",
            error
          );
        }
        setShowDropdown(false);
        setUserHasTyped(false); // Reset flag since user selected from dropdown
      }
      return;
    }

    if (e.key === "Tab") {
      if (showDropdown && highlightedIndex != null) {
        // If user has actively typed something, let blur handler validate it
        // Otherwise, auto-select the highlighted dropdown option
        if (userHasTyped) {
          setShowDropdown(false);
          // Let the blur handler process the typed input
          return;
        }

        // Auto-select the highlighted option when user hasn't typed
        const highlightedTime = timeOptions[highlightedIndex];
        setInputValue(highlightedTime);
        try {
          onChange(highlightedTime);
        } catch (error) {
          console.warn(
            "TimePickerInput: onChange callback failed during Tab key auto-select:",
            error
          );
        }
        setShowDropdown(false);
        setUserHasTyped(false);
      }
      return;
    }

    if (e.key === "Escape") {
      if (showDropdown) {
        e.preventDefault();
        setShowDropdown(false);
      }
      return;
    }
  };

  return (
    <div
      className={styles.timePickerContainer}
      onMouseDown={handleContainerMouseDown}
      ref={containerRef}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        onFocus={() => !disabled && setShowDropdown(true)}
        disabled={disabled}
        className={`${styles.timeInput} ${className || ""}`}
        placeholder="HH:mm"
      />
      {showDropdown &&
        !disabled &&
        ReactDOM.createPortal(
          <ul
            ref={dropdownRef}
            className={styles.timeDropdown}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {timeOptions.map((time, idx) => (
              <li
                key={time}
                className={`${styles.timeOption} ${
                  highlightedIndex === idx ? styles.highlighted : ""
                }`}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {time}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};
